/**
 * @file    WorkerPanel.css - Styles the worker panel component.
 *
 * @author  Kirill Kirnichansky <kirill@distributive.network>
 * @author  Eddie Roosenmaallen <eddie@distributive.network>
 * @author  Kevin Yu <kevin@distributive.network>
 *
 * @date    July 2022
 */

.worker-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.worker-page h1 {
  font-size: 24px;
}

@media only screen and (max-width: 768px) {
  .worker-page h1 {
    text-align: center;
  }
}

.worker-page label {
  color: black;
  font-size: 18px;
  font-weight: bold;
}

@media only screen and (max-width: 768px) {
  .worker-page label {
    text-align: center;
  }
}

.worker-sandbox-progress-container {
  position: relative;
  width: 100%;
  height: 24px;
  background-color: #e2e2e2;

  &:focus-visible {
    outline:          2px solid var(--primary-hue-lighter);
    outline-offset:   2px;
    border-radius:    0.5px;
  
    z-index: 1;
  }
}

.worker-sandbox-progress-container.indeterminate {
  background: repeating-linear-gradient(
    45deg,
    #00000010,
    #00000010 10px,
    #00000020 10px,
    #00000020 20px
  );

  animation: barberpole 0.9s linear infinite;
}

.worker-sandbox-progress-container.indeterminate:before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  transition: background-color 0.3s ease-out;
  background-color: rgba(112, 196, 150, 0);
}

.worker-sandbox-progress-container.indeterminate.ping:before {
  transition: background-color 0.1s ease-in;
  background-color: rgba(112, 196, 150, 0.5);
}

.worker-sandbox-progress-bar {
  width: 0%;
  background-color: #00a473;
  height: 24px;
  transition: 100ms width;
}

.worker-sandbox-progress-bar[progress="0"] {
  transition: none !important;
}

.worker-sandbox-progress-bar[progress="100"] {
  transition: 500ms background-color linear, width none;
  background-color: #c4c4c4;
}

@keyframes barberpole {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 60px 0;
  }
}

.worker-sandbox-progress-label {
  color: #3a3a3a;
  font-weight: bold;
  width: 100%;
  position: absolute;
  top: 0px;
  height: 24px;
  line-height: 24px;
  text-align: center;

  overflow: hidden;
  text-wrap: nowrap;
  text-overflow: ellipsis;
}

.worker-page .worker-page-content {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.currentStatusTitle {
  display: flex;
  column-gap: 2em;
  justify-content: center;
}

.worker-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 30px 90px;
  padding: 1.5em;
  width: 100%;
  background-color: var(--content-hue-bg);
  border: solid 1px var(--content-hue-main);
  min-height: 150px;
}

@media only screen and (max-width: 1024px) {
  .worker-section {
    gap: 30px 60px;
  }
}

@media only screen and (max-width: 768px) {
  .worker-section {
    grid-template-columns: 1fr;
  }

  #run-worker {
    grid-row-start: 3;
  }
}

.worker-config-option {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.worker-config-option button {
  text-wrap: nowrap;
}

#cpu-cores-input {
  width: 4rem;
  text-align: center;
  font-size: 1.2em;
  font-weight: bold;
  padding-left: 0;
}

@media only screen and (max-width: 768px) {
  .worker-config-option button,
  #cpu-cores-input {
    width: 100%;
  }
}

#cpu-cores-input {
  -moz-appearance: textfield;
}

.worker-page .worker-stats .dash-metric {
  font-size: 3em;
  color: var(--primary-hue-main);
  justify-content: start;
  font-weight: normal;
  text-wrap: nowrap;
  overflow-x: auto;
}

@media only screen and (max-width: 1280px) {
  .worker-page .dash-metric {
    font-size: 36px;
  }
}

@media only screen and (max-width: 768px) {
  .worker-page .dash-metric {
    text-align: center;
    justify-content: center;
    overflow: initial;
  }
}

.worker-stats {
  display: flex;
  justify-content: start;
  flex-direction: column;
  gap: 20px;
  overflow: hidden;

  label {
    text-wrap: nowrap;
  }

  > div {
    /* Used to make metrics overflow when on mobile devices */
    overflow: auto;
    width: -webkit-fill-available; 
    width: -moz-available; 
    width: fill-available; 
  }

  .dcc,
  .dash-metric {
    width: max-content;
    margin-right: auto;
    display: flex;
  }
}

.time-stats {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
}

@media only screen and (max-width: 768px) {
  .time-stats {
    display: flex;
    justify-content: center;
    gap: 40px;
  }

  .worker-stats {
    align-items: center;

    .dash-metric {
      margin: auto;
    }
  }
}

.time-stats > div {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
}

.worker-page .worker-status-title {
  background-color: #c4c4c4;
  border-bottom: 1px solid var(--content-hue-main);
  color: white;
  text-align: center;
  padding: 0.5em;
}

.worker-page .worker-status-title label {
  position: relative;
}

/* Change the progress bar on sandbox-progress */
.worker-sandbox-progress-bar {
  background-color: var(--primary-hue-main);
}

@-webkit-keyframes progress-bar-stripes {
  from {
    background-position: 50px 0;
  }
  to {
    background-position: 0 0;
  }
}

@keyframes progress-bar-stripes {
  from {
    background-position: 50px 0;
  }
  to {
    background-position: 0 0;
  }
}

.progress-bar-animated {
  -webkit-animation: progress-bar-stripes 1s linear infinite;
  animation: progress-bar-stripes 1s linear infinite;
}

.progress-bar-striped {
  background-image: linear-gradient(
    45deg,
    hsla(0, 0%, 82%, 0.15) 25%,
    transparent 0,
    transparent 50%,
    hsla(0, 0%, 82%, 0.15) 0,
    hsla(0, 0%, 82%, 0.15) 75%,
    transparent 0,
    transparent
  );
  background-size: 1rem 1rem;
}

.ellipses {
  position: absolute;
  bottom: 0;
  right: 0;
  transform: translateX(110%);
}

.ellipses::after {
  display: inline-block;
  animation: dotty steps(1, end) 2s infinite;
  content: "";
  height: 100%;
  line-height: 0;
  align-items: center;
}

@keyframes dotty {
  0% {
    content: "";
  }

  25% {
    content: ".";
  }

  50% {
    content: "..";
  }

  75% {
    content: "...";
  }

  100% {
    content: "";
  }
}

.zero-sandbox-msgs {
  text-align: center;
}

.zero-sandbox-msgs,
.worker-sandboxes-container {
  padding: 1rem 2rem;
  min-height: 60px;
  align-content: center;
}

.worker-sandboxes-container {
  display: flex;
  flex-direction: column;
  row-gap: 10px;

  button {
    width: 100%;
  }
}

.worker-sandbox-tooltip-content {
  span:first-child {
    font-weight: bold;
    font-size: 0.875rem;
  }

  span:last-child {
    font-size: 0.75rem;
  }
}
